import { LoadStatus } from '@servicetitan/cp-ui';
import { CheckboxFieldState, FormValidators, InputFieldState } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';
import { FormState } from 'formstate';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { validateContactInfo } from '../utils/contact-info.validator';
import { normalizeContactForSubmit } from '../utils/contact-method-mode';
import { requiredPasswordFieldValidator } from '../utils/password.validator';
import { LoginResult, PortalAuthStore } from './portal-auth.store';
import { SettingsStore } from './settings.store';

/** Required-field message for each mode (AC-5: required check fires first, I1). */
const requiredMessageByMode = {
    'email-only': 'Enter an email',
    'phone-only': 'Enter a phone number',
    'either': 'Enter an email or phone number',
} as const;

/** Format-error message for each mode (AC-5). */
const formatErrorByMode = {
    'email-only': 'Enter a valid email address',
    'phone-only': 'Enter a valid phone number',
    'either': 'Enter a valid email or phone number',
} as const;

@injectable()
export class AuthLoginStore {
    @observable submitStatus = LoadStatus.None;
    @observable submitResult?: LoginResult;

    @computed get canSubmit() {
        return !this.form.$.email.hasError;
    }

    form = new FormState({
        email: new InputFieldState('')
            .validators(
                /*
                 * Required check FIRST — formstate short-circuits in order (I1).
                 * Arrow closures read this.settings.contactInfoMode lazily at validate time.
                 */
                (val: string) =>
                    FormValidators.requiredWithCustomMessage(
                        requiredMessageByMode[this.settings.contactInfoMode]
                    )(val),
                (val: string) =>
                    !validateContactInfo(val, this.settings.contactInfoMode).isValid &&
                    formatErrorByMode[this.settings.contactInfoMode]
            )
            .disableAutoValidation(),
        password: new InputFieldState('')
            .validators(requiredPasswordFieldValidator)
            .disableAutoValidation(),
        keepSigned: new CheckboxFieldState(true),
    });

    private isInviteFlow = false;

    constructor(
        @inject(PortalAuthStore) private authStore: PortalAuthStore,
        @inject(SettingsStore) private settings: SettingsStore
    ) {
        makeObservable(this);
    }

    init = (code?: string) => {
        this.isInviteFlow = !!code;
    };

    submit = async () => {
        await this.form.validate();

        if (!this.canSubmit) {
            return;
        }

        const { email, keepSigned, password } = this.form.$;

        runInAction(() => (this.submitStatus = LoadStatus.Loading));

        const loginResult = await this.authStore.login(
            normalizeContactForSubmit(email.value, this.settings.contactInfoMode),
            password.value,
            keepSigned.value
        );

        runInAction(() => {
            this.submitStatus =
                !loginResult ||
                loginResult === LoginResult.MfaRequired ||
                (loginResult === LoginResult.Restricted && this.isInviteFlow)
                    ? LoadStatus.Ok
                    : LoadStatus.Error;
            this.submitResult = loginResult;

            if (loginResult === LoginResult.Invalid) {
                this.form.$.password.setError('Incorrect password');
                this.form.$.password.enableAutoValidation();
            }
        });
    };
}
